import { addDaysKey, osloDateKey } from "./osloDate";

// Virkedager i Europe/Oslo (pipeline v2.1, kap. 5.1): helger og norske
// helligdager teller ikke. De bevegelige helligdagene regnes ut fra påske, så
// vi trenger ingen ekstern tjeneste. Alle datoer er kalenderdatoer
// (YYYY-MM-DD), som osloDateKey gir.

// Påskedag (gregoriansk), den anonyme algoritmen (Meeus/Jones/Butcher).
export const easterSundayKey = (year: number): string => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const holidayCache = new Map<number, Set<string>>();

export const norwegianHolidays = (year: number): Set<string> => {
  const cached = holidayCache.get(year);
  if (cached) return cached;
  const easter = easterSundayKey(year);
  const fixed = ["01-01", "05-01", "05-17", "12-25", "12-26"].map(
    (md) => `${year}-${md}`,
  );
  // Skjærtorsdag, langfredag, påskedag, 2. påskedag, Kristi himmelfart,
  // pinsedag og 2. pinsedag.
  const moving = [-3, -2, 0, 1, 39, 49, 50].map((offset) =>
    addDaysKey(easter, offset),
  );
  const days = new Set([...fixed, ...moving]);
  holidayCache.set(year, days);
  return days;
};

const weekday = (key: string) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};

export const isWorkday = (key: string): boolean => {
  const day = weekday(key);
  if (day === 0 || day === 6) return false;
  return !norwegianHolidays(Number(key.slice(0, 4))).has(key);
};

// Dagen selv hvis den er en virkedag, ellers neste virkedag.
export const nextWorkdayOnOrAfter = (key: string): string => {
  let current = key;
  while (!isWorkday(current)) current = addDaysKey(current, 1);
  return current;
};

// n virkedager etter datoen. 0 gir dagen selv (eller neste virkedag hvis den
// er fri).
export const addWorkdays = (key: string, n: number): string => {
  let current = nextWorkdayOnOrAfter(key);
  let left = Math.max(0, Math.floor(n));
  while (left > 0) {
    current = addDaysKey(current, 1);
    if (isWorkday(current)) left -= 1;
  }
  return current;
};

// Antall virkedager etter fra og til og med til. Samme dag gir 0.
export const workdaysBetween = (fromKey: string, toKey: string): number => {
  if (toKey <= fromKey) return 0;
  let count = 0;
  let current = fromKey;
  // Tak på ti år, så en feil dato ikke gir en evig løkke.
  for (let i = 0; i < 3660 && current < toKey; i++) {
    current = addDaysKey(current, 1);
    if (isWorkday(current)) count += 1;
  }
  return count;
};

// Kalenderdager mellom to datoer (til minus fra).
export const daysBetween = (fromKey: string, toKey: string): number => {
  const toUtc = (key: string) => {
    const [y, m, d] = key.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((toUtc(toKey) - toUtc(fromKey)) / 86_400_000);
};

const hourFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Oslo",
  hour: "2-digit",
  hourCycle: "h23",
});

// Klokketimen (0–23) i Europe/Oslo.
export const osloHour = (date: Date = new Date()): number =>
  Number(hourFormatter.format(date));

export const todayKey = () => osloDateKey();
